import { createSlice } from "@reduxjs/toolkit";

const currentFileInfo = localStorage.getItem("currentFileInfo") !== null ? JSON.parse(localStorage.getItem("currentFileInfo")) : [];

const appSlice = createSlice({
    name: "app",
    initialState: {
        sidebarData: null,
        homeData: null,
        botData: null,
        sidebarError: null,
        homeDataError: null,
        currentFileInfo: currentFileInfo,
        shouldShowAttachment: false,
        sidebarSearchData: null,
        shouldRefreshSession: false,
        shouldDisableInput: false,
        isTokenExpired: false,
        isTimerVisible: false,
        timerSeconds: 0,
        quicklinkQuestions: null,
        commonWords: null,
        showTour: false,
        CarouselData: null,
        whatsNewLoading: false,
        whatsNewError: null,
        whatsNewDetailItem: null,
        whatsNewDetailLoading: false,
        // Use case questions state - cached by botType
        useCaseQuestions: {},
        useCaseQuestionsLoading: false,
        useCaseQuestionsError: null,
        // Notification state
        notifications: [],
        pendingApprovals: [],
        notificationLoading: false,
        // File sync state for riwa_gpt
        isSyncing: false,
        syncFileName: null,
        syncFileSize: null,
        syncingFilesCount: 0,
        lastCompletedSyncFile: null,
        lastSyncSuccess: true
    },
    reducers: {
        addSidebarData: (state, action) => {
            state.sidebarData = action.payload
        },
        addHomeData: (state, action) => {
            state.homeData = action.payload;
        },
        addSidebarError: (state, action) => {
            state.sidebarError = action.payload;
        },
        addHomeDataError: (state, action) => {
            state.homeDataError = action.payload;
        },
        addBotData: (state, action) => {
            state.botData = action.payload;
        },
        addFileKey: (state, action) => {
            state.currentFileInfo = [...state.currentFileInfo, action.payload];
            localStorage.setItem("currentFileInfo", JSON.stringify(state.currentFileInfo));
        },
        removeFileKey: (state, action) => {
            state.currentFileInfo = state.currentFileInfo.filter((key) => key.fileKey !== action.payload);
            localStorage.setItem("currentFileInfo", JSON.stringify(currentFileInfo));
        },
        clearFileArray: (state) => {
            state.currentFileInfo = [];
            localStorage.setItem("currentFileInfo", JSON.stringify([]));
        },
        setShoulldShowAttachment: (state, action) => {
            state.shouldShowAttachment = action.payload;
        },
        setSidebarSearchData: (state, action) => {
            state.sidebarSearchData = action.payload;
        },
        setShouldRefreshSession: (state, action) => {
            state.shouldRefreshSession = action.payload;
        },
        setShouldDisableInput: (state, action) => {
            state.shouldDisableInput = action.payload
        },
        setTokenExpired: (state) => {
            state.isTokenExpired = true;
        },
        setTimer: (state, action) => {
            state.isTimerVisible = action.payload.isTimerVisible;
            state.timerSeconds = action.payload.timerSeconds
        },
        addQuickLinkQuestion: (state, action) => {
            state.quicklinkQuestions = action.payload;
        },
        addCommonWords: (state, action) => {
            state.commonWords = action.payload;
        },
        setShowTour: (state, action) => {
            state.showTour = action.payload;
        },
        setCarouselData: (state, action) => {
            state.CarouselData = action.payload;
        },
        // Use case questions reducers
        setUseCaseQuestions: (state, action) => {
            const { botType, questions } = action.payload;
            state.useCaseQuestions[botType] = questions;
        },
        clearUseCaseQuestions: (state, action) => {
            if (action.payload) {
                // Clear specific botType
                delete state.useCaseQuestions[action.payload];
            } else {
                // Clear all
                state.useCaseQuestions = {};
            }
        },
        setUseCaseQuestionsLoading: (state, action) => {
            state.useCaseQuestionsLoading = action.payload;
        },
        setUseCaseQuestionsError: (state, action) => {
            state.useCaseQuestionsError = action.payload;
        },
        // Notification reducers
        setNotifications: (state, action) => {
            state.notifications = action.payload;
        },
        setPendingApprovals: (state, action) => {
            state.pendingApprovals = action.payload;
        },
        setNotificationLoading: (state, action) => {
            state.notificationLoading = action.payload;
        },
        addNotification: (state, action) => {
            state.notifications.unshift(action.payload);
        },
        markNotificationRead: (state, action) => {
            const notifId = action.payload;
            const notif = state.notifications.find(n => n.id === notifId);
            if (notif) {
                notif.isRead = true;
            }
        },
        markAllNotificationsRead: (state) => {
            state.notifications.forEach(notif => {
                notif.isRead = true;
            });
        },
        clearNotifications: (state) => {
            state.notifications = [];
            state.pendingApprovals = [];
        },
        // What's New reducers
        setWhatsNewLoading: (state, action) => {
            state.whatsNewLoading = action.payload;
        },
        setWhatsNewError: (state, action) => {
            state.whatsNewError = action.payload;
        },
        setWhatsNewDetailItem: (state, action) => {
            state.whatsNewDetailItem = action.payload;
        },
        setWhatsNewDetailLoading: (state, action) => {
            state.whatsNewDetailLoading = action.payload;
        },
        clearWhatsNewDetail: (state) => {
            state.whatsNewDetailItem = null;
            state.whatsNewDetailLoading = false;
        },
        // File sync reducers for riwa_gpt
        setSyncing: (state, action) => {
            state.syncingFilesCount = (state.syncingFilesCount || 0) + 1;
            state.isSyncing = state.syncingFilesCount > 0;
            state.syncFileName = action.payload.fileName || null;
            state.syncFileSize = action.payload.fileSize || null;
        },
        clearSyncing: (state, action) => {
            // Payload: { fileName, success } or just a fileName string
            const payload = action.payload;
            const fileName = typeof payload === 'string' ? payload : payload?.fileName;
            const success = typeof payload === 'string' ? true : (payload?.success ?? true);
            state.syncingFilesCount = Math.max(0, (state.syncingFilesCount || 0) - 1);
            state.isSyncing = state.syncingFilesCount > 0;
            state.lastCompletedSyncFile = fileName || null;
            state.lastSyncSuccess = success;
        },
        clearSyncFile: (state) => {
            // Clear all sync file state (used when starting new session)
            state.isSyncing = false;
            state.syncFileName = null;
            state.syncFileSize = null;
            state.syncingFilesCount = 0;
            state.lastCompletedSyncFile = null;
            state.lastSyncSuccess = true;
        }
    }
});

export const { addSidebarData, addHomeData, addSidebarError, addHomeDataError, addFileKey, removeFileKey, clearFileArray, setShoulldShowAttachment, setSidebarSearchData, setShouldRefreshSession, setShouldDisableInput, addBotData, setTokenExpired, setTimer, addQuickLinkQuestion, addCommonWords, setShowTour, setCarouselData, setUseCaseQuestions, clearUseCaseQuestions, setUseCaseQuestionsLoading, setUseCaseQuestionsError, setNotifications, setPendingApprovals, setNotificationLoading, addNotification, markNotificationRead, markAllNotificationsRead, clearNotifications, setWhatsNewLoading, setWhatsNewError, setWhatsNewDetailItem, setWhatsNewDetailLoading, clearWhatsNewDetail, setSyncing, clearSyncing, clearSyncFile } = appSlice.actions;
export default appSlice.reducer;
